import { useState } from "react"
import { ArrowRight, Tag, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

// The admin Sales Promotion as the dashboard's split card (design handoff
// 2026-10-12, concept B: text on a plain surface, media beside it). Every
// field is optional; the presenter sends null when nothing can render.
// Buttons are derived because admin has no label field: a link gives
// "Learn more" (+ "Details" when there is a description); otherwise a
// description opens the modal as "View details", or "Sign up" for an embedded
// form. No title means a designed banner: the whole card is the click target,
// opening the details when there are any, else following the link.

const DISMISS_KEY = "tuw-promo-dismissed"

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY)
  } catch {
    return null
  }
}

function writeDismissed(id) {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(id))
  } catch {
    // Storage blocked: the dismiss still holds for this page view.
  }
}

function postedLabel(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function PromoMedia({ media, className }) {
  if (!media?.url) return null
  const fill = "absolute inset-0 size-full object-cover object-center"

  return (
    <div
      data-testid="promo-media"
      className={cn("relative aspect-[21/9] w-full overflow-hidden bg-secondary", className)}
    >
      {media.kind === "video" ? (
        <video
          src={media.url}
          className={cn(fill, "motion-reduce:hidden")}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <img src={media.url} alt="" className={fill} />
      )}
      {media.kind === "video" && (
        // Under reduced motion the autoplaying copy is hidden and this one
        // waits for the customer to press play.
        <video
          src={media.url}
          className={cn(fill, "hidden motion-reduce:block")}
          controls
          muted
          playsInline
          preload="metadata"
        />
      )}
    </div>
  )
}

function PromoTag({ posted }) {
  return (
    <span className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.08em] text-primary uppercase">
      <Tag className="size-3" strokeWidth={2.2} />
      Promotion
      {posted && (
        <i className="font-semibold tracking-[0.04em] text-ink-3 not-italic">· Posted {posted}</i>
      )}
    </span>
  )
}

function LearnMore({ href, className }) {
  return (
    <Button asChild className={cn("font-display", className)}>
      <a href={href} data-testid="promo-learn-more">
        Learn more <ArrowRight strokeWidth={2.2} />
      </a>
    </Button>
  )
}

function PromoModal({ promo, open, onOpenChange, posted }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="promo-modal"
        className="gap-0 overflow-x-hidden p-0 sm:max-w-[580px]"
        showCloseButton={false}
      >
        {/* The design's close chip: opaque so it reads over the media. */}
        <DialogClose
          aria-label="Close"
          className="absolute top-2.5 right-2.5 z-[1] grid size-[30px] place-items-center rounded-lg bg-card text-foreground"
        >
          <X className="size-[15px]" strokeWidth={2.2} />
        </DialogClose>
        <PromoMedia media={promo.media} />
        <div className="px-6 pt-[22px] pb-2">
          <PromoTag posted={posted} />
          <DialogTitle className="mt-1.5 font-display text-[21px] leading-tight font-bold tracking-[-0.3px]">
            {promo.title || "Current promotion"}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="mt-3 text-sm leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline [&_b]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-[18px]">
              {promo.formHtml ? (
                // Admin-pasted form HTML runs only in a sandbox with no access
                // to the app's origin, cookies or session.
                <iframe
                  title={promo.title || "Promotion form"}
                  data-testid="promo-form"
                  srcDoc={promo.formHtml}
                  sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                  className="h-[420px] w-full rounded-lg border border-border bg-card"
                />
              ) : (
                promo.descriptionHtml && (
                  <div
                    data-testid="promo-description"
                    // ActionText's sanitized render, the same HTML the legacy
                    // dashboard widget showed.
                    dangerouslySetInnerHTML={{ __html: promo.descriptionHtml }}
                  />
                )
              )}
            </div>
          </DialogDescription>
        </div>
        <div className="flex justify-end gap-2 px-6 pt-4 pb-[22px]">
          {promo.link && <LearnMore href={promo.link} />}
          <Button
            variant="outline"
            className="font-display"
            data-testid="promo-close"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default function DashboardPromo({ promo }) {
  const [dismissedId, setDismissedId] = useState(readDismissed)
  const [modalOpen, setModalOpen] = useState(false)

  // Admin edits one record in place, so a dismiss is keyed to this version of
  // it: saving a new promotion brings the card back.
  const dismissKey = `${promo?.id}:${promo?.postedOn ?? ""}`
  if (!promo || dismissKey === dismissedId) return null

  const posted = postedLabel(promo.postedOn)
  const hasDetails = Boolean(promo.descriptionHtml || promo.formHtml)
  const hasMedia = Boolean(promo.media?.url)

  const dismiss = (event) => {
    event.stopPropagation()
    writeDismissed(dismissKey)
    setDismissedId(dismissKey)
  }

  const dismissButton = (chipClass) => (
    <button
      type="button"
      aria-label="Dismiss promotion"
      data-testid="promo-dismiss"
      onClick={dismiss}
      className={cn(
        "absolute top-2.5 right-2.5 z-[1] grid size-[30px] place-items-center rounded-lg text-ink-3 hover:bg-secondary hover:text-foreground",
        chipClass
      )}
    >
      <X className="size-[15px]" strokeWidth={2.2} />
    </button>
  )

  const modal = hasDetails && (
    <PromoModal promo={promo} open={modalOpen} onOpenChange={setModalOpen} posted={posted} />
  )

  // Designed banner: no title, the image carries the message.
  if (!promo.title) {
    if (!hasMedia) return null
    // Details open the modal (which carries Learn more); a bare banner follows
    // its link.
    const activate = () => {
      if (hasDetails) setModalOpen(true)
      else if (promo.link) window.location.assign(promo.link)
    }

    return (
      <>
        <div
          data-testid="promo-card"
          role="link"
          tabIndex={0}
          aria-label="View promotion"
          onClick={activate}
          onKeyDown={(event) => {
            // Keys pressed on the nested dismiss button are its own.
            if (event.target !== event.currentTarget) return
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              activate()
            }
          }}
          className="relative cursor-pointer overflow-hidden rounded-2xl border border-border bg-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <PromoMedia media={promo.media} className="aspect-[21/6]" />
          {dismissButton("bg-card text-foreground")}
        </div>
        {modal}
      </>
    )
  }

  return (
    <>
      <div
        data-testid="promo-card"
        className={cn(
          "relative grid overflow-hidden rounded-2xl border border-border bg-card",
          hasMedia && "min-[861px]:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]"
        )}
      >
        <div className="flex min-w-0 flex-col justify-center gap-[9px] px-[26px] py-6 max-[561px]:px-[18px] max-[561px]:pt-[18px] max-[561px]:pb-5">
          <PromoTag posted={posted} />
          <h3 className="pr-7 font-display text-[21px] leading-tight font-bold tracking-[-0.3px] text-balance max-[561px]:text-lg">
            {promo.title}
          </h3>
          {promo.excerpt && (
            <p data-testid="promo-excerpt" className="line-clamp-2 text-[13.5px] leading-normal text-muted-foreground">
              {promo.excerpt}
            </p>
          )}
          {(promo.link || hasDetails) && (
            <div className="mt-1.5 flex flex-wrap gap-2">
              {promo.link ? (
                <>
                  <LearnMore href={promo.link} className="max-[561px]:flex-auto" />
                  {hasDetails && (
                    <Button
                      variant="outline"
                      className="font-display max-[561px]:flex-auto"
                      onClick={() => setModalOpen(true)}
                    >
                      Details
                    </Button>
                  )}
                </>
              ) : (
                <Button className="font-display max-[561px]:flex-auto" onClick={() => setModalOpen(true)}>
                  {promo.formHtml ? "Sign up" : "View details"} <ArrowRight strokeWidth={2.2} />
                </Button>
              )}
            </div>
          )}
        </div>
        {hasMedia && <PromoMedia media={promo.media} className="h-full max-[861px]:order-first" />}
        {/* Media stacks on top at ≤860, so the × needs a chip to read over it. */}
        {dismissButton(hasMedia && "max-[861px]:bg-card max-[861px]:text-foreground")}
      </div>
      {modal}
    </>
  )
}
